/**
 * 🔌 CONSTANTES SOCKET.IO - Événements Standardisés (FRONTEND)
 *
 * Ce fichier définit TOUS les événements Socket.IO utilisés dans l'application.
 * Il garantit la synchronisation parfaite entre frontend et backend.
 *
 * ⚠️ IMPORTANT: Ces constantes doivent être identiques côté backend !
 */

export const SOCKET_EVENTS = {
  // ========================================
  // CONNEXION / AUTHENTIFICATION
  // ========================================

  /** Client s'authentifie auprès du serveur */
  AUTHENTICATE: 'authenticate',

  /** Serveur confirme l'authentification */
  AUTHENTICATED: 'authenticated',

  /** Erreur d'authentification */
  AUTH_ERROR: 'auth:error',

  // ========================================
  // PRÉSENCE UTILISATEUR
  // ========================================

  /** Utilisateur vient de se connecter */
  USER_CONNECTED: 'user:connected',

  /** Utilisateur vient de se déconnecter */
  USER_DISCONNECTED: 'user:disconnected',

  /** Statut d'un utilisateur a changé (ONLINE/OFFLINE/AWAY) */
  USER_STATUS_CHANGED: 'user:status:changed',

  /** Utilisateur est maintenant en ligne */
  USER_ONLINE: 'user:online',

  /** Utilisateur est maintenant hors ligne */
  USER_OFFLINE: 'user:offline',

  /** Utilisateur est maintenant absent (away) */
  USER_AWAY: 'user:away',

  // ========================================
  // DISPONIBILITÉ DOCTEUR
  // ========================================

  /** Disponibilité d'un docteur a changé (AVAILABLE/BUSY/OFFLINE) */
  DOCTOR_AVAILABILITY_CHANGED: 'doctor:availability:changed',

  /** Mise à jour du statut d'un docteur */
  DOCTOR_STATUS_UPDATE: 'doctor:status:update',

  /** Docteur définit sa disponibilité */
  DOCTOR_SET_AVAILABILITY: 'doctor:set_availability',

  // ========================================
  // ADMIN - SUPERVISION
  // ========================================

  /** Liste des utilisateurs en ligne (pour admin) */
  ADMIN_USERS_ONLINE: 'admin:users:online',

  /** Activité d'un utilisateur (pour admin) */
  ADMIN_USER_ACTIVITY: 'admin:user:activity',

  /** Statistiques de présence (pour admin) */
  ADMIN_PRESENCE_STATS: 'admin:presence:stats',

  // ========================================
  // TÉLÉCONSULTATION
  // ========================================

  /** Rejoindre une salle d'attente */
  TELECONSULT_JOIN_WAITING_ROOM: 'teleconsultation:join_waiting_room',

  /** Battement de coeur pour maintenir la session active */
  TELECONSULT_HEARTBEAT: 'teleconsultation:heartbeat',

  /** Quitter une salle d'attente */
  TELECONSULT_LEAVE_WAITING_ROOM: 'teleconsultation:leave_waiting_room',

  /** Rejoindre la vue globale de la salle d'attente (Médecins) */
  WAITING_ROOM_JOIN: 'waiting_room:join',

  /** Quitter la vue globale de la salle d'attente (Médecins) */
  WAITING_ROOM_LEAVE: 'waiting_room:leave',

  /** Docteur assigné à un patient */
  DOCTOR_ASSIGNED: 'doctor_assigned',

  /** Handshake pré-consultation : tokens Agora envoyés aux participants (avant IN_PROGRESS) */
  CONSULTATION_HANDSHAKE: 'consultation:handshake',

  /** Consultation a démarré */
  CONSULTATION_STARTED: 'consultation_started',

  /** Consultation terminée */
  CONSULTATION_ENDED: 'consultation_ended',

  // --- PEER CONSULTATION (Entre Pros de Santé) ---
  /** Une consultation peer a démarré (P2P) */
  PEER_CONSULTATION_STARTED: 'peer_consultation:started',

  /** Une consultation peer a été terminée (P2P) */
  PEER_CONSULTATION_ENDED: 'peer_consultation:ended',

  /** Consultation annulée */
  CONSULTATION_CANCELLED: 'consultation_cancelled',

  /** Participant en cours de reconnexion (perte réseau détectée) */
  CONSULTATION_RECONNECTING: 'consultation:reconnecting',

  /** Consultation mise en pause (timeout reconnexion dépassé) */
  CONSULTATION_PAUSED: 'consultation:paused',

  /** Consultation reprise après reconnexion */
  CONSULTATION_RESUMED: 'consultation:resumed',

  /** Une consultation peer a été demandée */
  PEER_CONSULTATION_REQUESTED: 'peer_consultation:requested',

  /** File d'attente mise à jour */
  QUEUE_UPDATED: 'queue_updated',

  /** Nouveau patient en attente */
  NEW_PATIENT_WAITING: 'new_patient_waiting',

  /** Patient a quitté la file */
  PATIENT_LEFT_QUEUE: 'patient_left_queue',

  /** Patient est en cours de traitement par un autre médecin (verrou visuel) */
  WAITING_ROOM_PATIENT_LOCKED: 'waiting_room:patient:locked',

  /** Patient n'est plus en cours de traitement (déverrouillage visuel) */
  WAITING_ROOM_PATIENT_UNLOCKED: 'waiting_room:patient:unlocked',

  /** Conversation complète marquée comme lue */
  CONVERSATION_READ: 'conversation:read',

  /** File d'attente complète mise à jour */
  WAITING_QUEUE_UPDATED: 'waiting_queue_updated',

  // ========================================
  // WEBRTC SIGNALING
  // ========================================

  /** Rejoindre un canal WebRTC */
  WEBRTC_JOIN: 'webrtc:join',

  /** Quitter un canal WebRTC */
  WEBRTC_LEAVE: 'webrtc:leave',

  /** Utilisateur a rejoint le canal */
  WEBRTC_USER_JOINED: 'webrtc:user_joined',

  /** Utilisateur a quitté le canal */
  WEBRTC_USER_LEFT: 'webrtc:user_left',

  /** Message de signaling WebRTC */
  WEBRTC_SIGNALING: 'webrtc:signaling',

  // ========================================
  // APPELS VIDÉO
  // ========================================

  /** Démarrer un appel */
  CALL_START: 'call:start',

  /** Appel entrant */
  CALL_INCOMING: 'call:incoming',

  /** Accepter un appel */
  CALL_ACCEPT: 'call:accept',

  /** Appel accepté */
  CALL_ACCEPTED: 'call:accepted',

  /** Rejeter un appel */
  CALL_REJECT: 'call:reject',

  /** Appel rejeté */
  CALL_REJECTED: 'call:rejected',

  /** Terminer un appel */
  CALL_END: 'call:end',

  /** Appel terminé */
  CALL_ENDED: 'call:ended',

  /** Mute/unmute audio */
  CALL_MUTE: 'call:mute',

  /** Audio muted/unmuted */
  CALL_MUTED: 'call:muted',

  /** Toggle vidéo */
  CALL_VIDEO_TOGGLE: 'call:video_toggle',

  /** Vidéo toggled */
  CALL_VIDEO_TOGGLED: 'call:video_toggled',

  /** Statut d'appel vidéo changé */
  VIDEO_CALL_STATUS_CHANGED: 'video_call_status_changed',

  /** Relancer un patient (Notification) */
  CALL_RELAUNCHED: 'call_relaunched',

  // ========================================
  // MESSAGERIE COUCHE 1 : CLASSIC (DB)
  // ========================================
  JOIN_CONVERSATION: 'join_conversation',
  LEAVE_CONVERSATION: 'leave_conversation',
  TYPING: 'typing',
  MESSAGE_NEW: 'message:new',
  MESSAGE_READ: 'message:read',
  MESSAGE_DELETED: 'message:deleted',
  MESSAGE_SEND: 'message:send',
  MESSAGE_SENT: 'message:sent', // ACK pour Classic
  MESSAGE_RECEIVED: 'message:received',

  // ========================================
  // MESSAGERIE COUCHE 2 : SALLE D'ATTENTE (REDIS)
  // ========================================
  WAITING_ROOM_CHAT_JOIN: 'waiting_room_chat:join',
  WAITING_ROOM_CHAT_LEAVE: 'waiting_room_chat:leave',
  WAITING_ROOM_CHAT_SEND: 'waiting_room_chat:send',
  WAITING_ROOM_CHAT_MESSAGE: 'waiting_room_chat:message',
  WAITING_ROOM_CHAT_TYPING: 'waiting_room_chat:typing',
  WAITING_ROOM_CHAT_SENT: 'waiting_room_chat:sent', // ACK pour Redis
  WAITING_ROOM_CHAT_READ: 'waiting_room_chat:read',
  WAITING_ROOM_CHAT_READ_STATUS: 'waiting_room_chat:read_status',

  // ========================================
  // MESSAGERIE COUCHE 3 : CONFÉRENCE / MULTI-MÉDECINS (REDIS)
  // ========================================
  CONFERENCE_CHAT_JOIN: 'conference:chat:join',
  CONFERENCE_CHAT_LEAVE: 'conference:chat:leave',
  CONFERENCE_CHAT_SEND: 'conference:chat:send',
  CONFERENCE_CHAT_SENT: 'conference:chat:sent',
  CONFERENCE_CHAT_MESSAGE: 'conference:chat:message',
  CONFERENCE_CHAT_TYPING: 'conference:chat:typing',
  CONFERENCE_CHAT_HISTORY: 'conference:chat:history',
  CONFERENCE_CHAT_READ: 'conference:chat:read',
  CONFERENCE_CHAT_READ_STATUS: 'conference:chat:read_status',

  // ========================================
  // URGENCES
  // ========================================
  EMERGENCY_ALERT: 'emergency:alert',

  // ========================================
  // SYSTÈME
  // ========================================
  RECONNECT: 'reconnect',
  TELECONSULT_RESUME: 'teleconsultation:resume',
  DISCONNECT: 'disconnect',
  CONNECT_ERROR: 'connect_error',
  TELECONSULT_NOTIFICATION: 'teleconsultation_notification',
  PRESCRIPTION_CREATED: 'prescription_created',
  HEALTH_METRICS_UPDATED: 'health_metrics:updated',
  GLOBAL_BROADCAST: 'global:broadcast',
  NOTIFICATION_NEW: 'notification:new',
  NOTIFICATION_READ: 'notification:read',
  ERROR: 'error',
  CONSULTATION_HEARTBEAT: 'consultation:heartbeat',
  CONSULTATION_USER_DISCONNECTED: 'consultation:user_disconnected',
} as const;

/**
 * Type pour les noms d'événements Socket.IO
 */
export type SocketEventName = (typeof SOCKET_EVENTS)[keyof typeof SOCKET_EVENTS];

export interface UserStatusChangedPayload {
  userId: string;
  status: 'ONLINE' | 'OFFLINE' | 'AWAY';
  role?: 'PATIENT' | 'DOCTOR' | 'ADMIN' | 'PHARMACIST';
  timestamp: Date;
}

export interface DoctorAvailabilityChangedPayload {
  doctorId: string;
  isAvailable: boolean;
  availabilityStatus?: 'AVAILABLE' | 'BUSY' | 'OFFLINE';
  timestamp: Date;
}

export interface UserConnectedPayload {
  userId: string;
  role: 'PATIENT' | 'DOCTOR' | 'ADMIN' | 'PHARMACIST';
  timestamp: Date;
  name?: string;
  email?: string;
  availabilityStatus?: 'AVAILABLE' | 'BUSY' | 'OFFLINE';
  isAvailable?: boolean;
}

export interface UserDisconnectedPayload {
  userId: string;
  timestamp: Date;
}

export interface AdminUsersOnlinePayload {
  users: Array<{
    userId: string;
    role: 'PATIENT' | 'DOCTOR' | 'ADMIN';
    status: 'ONLINE' | 'OFFLINE' | 'AWAY';
    availabilityStatus?: 'AVAILABLE' | 'BUSY' | 'OFFLINE';
    connectedAt: Date;
    lastActivity?: string;
  }>;
  totalOnline: number;
  timestamp: Date;
}

export function isValidSocketEvent(event: string): event is SocketEventName {
  return Object.values(SOCKET_EVENTS).includes(event as SocketEventName);
}

export default SOCKET_EVENTS;
